import Controller from '@ember/controller'
import moment from 'moment-timezone';

import {
    action,
    computed,
    set
} from '@ember/object'

import {
    task,
    timeout
} from 'ember-concurrency'

import {
    inject as iService
} from '@ember/service'

import {
    inject as iController
} from '@ember/controller'

import { cfFilterId } from 'beebee-base/lib/utility';
import { beebeeAjaxPost, beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — product detail page: group-course calendar (buyGroupsTask),
 *              private-lesson availability/instructor search (loadPrivateTask,
 *              maestroTurnoTask), level/participant selection and add-to-cart pricing
 *              (freeGroupPriceTask, freePrivatePriceTask, addGroupsCartTask).
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        -
 *   key:       checkAvailabilityTask (guards against a transiently empty product list,
 *              redirects home if the product is truly gone), loadPrivateTask
 *              (/ecommerce_private_dashboard), buyGroupsTask (/ecommerce_dashboard for
 *              the calendar), maestroTurnoTask (/ecommerce_turno_maestro), checkBlockTask
 *              (/ecommerce_check_product before cart add), freeGroupPriceTask/
 *              freePrivatePriceTask (/scuolasci_freeprice), showBuyProductF, setComp/
 *              setLang/setDate/setFilter (@action, reload private data), addGroupsCart/
 *              addPrivateCart (@action, cart writes via applicationController.addCart).
 *   uses:      applicationController (@controller('application') — item source via
 *              prodotti.products, cart, location/instr_lang/date query state),
 *              beebee-base/lib/utility cfFilterId, lib/utility/ajax, lib/get-config
 *   siblings:  routes/detail.js (resets all selection state + kicks loadPrivateTask/
 *              checkAvailabilityTask on entry, cancels both on leave), ski-instructor.js
 *              (near-duplicate freePrivatePriceTask/checkBlockTask for the instructor-first
 *              private flow)
 *   tests:     detail-actions-test.js, detail-availability-test.js, detail-getters-test.js,
 *              detail-tasks-test.js, detail-groups-writes-test.js,
 *              detail-private-writes-test.js, detail-routes-test.js
 *   gotchas:   `item` is derived from `applicationController.prodotti.products` by id —
 *              availability is checked in checkAvailabilityTask (a task), not inside the
 *              `item` getter itself, so a transient empty products list during a reload
 *              can't bounce the user home. `intComp`/`intLang` are seeded from the
 *              application controller's location/instr_lang ONLY while unset (ghost-mode
 *              language/resort buttons write intLang/intComp directly) — re-seeding
 *              unconditionally on every loadPrivateTask perform used to revert the
 *              customer's own pick. freeGroupPriceTask/freePrivatePriceTask 4xx/network
 *              failures are user-triggered and notify (unlike loadPrivateTask/buyGroupsTask
 *              which log-only on background load failure).
 *
 * Product detail page controller: two parallel purchase flows (group course
 * calendar vs. private-lesson instructor search) feeding one shared cart.
 */
export default class DetailController extends Controller {
  @iService notify;
    @iService intl
    @iService session
    @iService store
    @iService router

    @iController('application') applicationController
    @iService log

    language = null
    gender = null
    //localita = null
    byGroupComp = null
    oreSelected = []
    oreSelectedDate = []

    @computed('session.option_field.lingue', 'applicationController.lang')
    get langFilters() {
      const opLingue = this.session.option_field?.lingue;
      const lingue = opLingue || ["italiano", "inglese", "francese", "tedesco"];
      return lingue;
    }

    setDefLang() {
      // Seeding only. `loadPrivateTask` runs this on EVERY perform, including
      // the perform a customer's own language button triggers, so it must never
      // overwrite a language that has already been chosen: in ghost mode the
      // language control writes `intLang` and leaves `instr_lang` empty, which
      // used to send this method straight back to the browser-locale default
      // and revert the pick before the request went out.
      if (this.intLang) {
        return;
      }

      const opLingue = this.session.option_field?.lingue;
      const lingue = opLingue || ["italiano", "inglese", "francese", "tedesco"];

      const currentLang = this.applicationController.instr_lang || this.applicationController.lang;
      let matchedLanguage = null;

      if (currentLang && currentLang.includes('-')) {
        switch (currentLang) {
          case 'it-it':
          matchedLanguage = lingue.includes('italiano') ? 'italiano' : null;
          break;
            case 'fr-fr':
          matchedLanguage = lingue.includes('francese') ? 'francese' : null;
          break;
            case 'de-de':
          matchedLanguage = lingue.includes('tedesco') ? 'tedesco' : null;
          break;
            case 'en-gb':
            case 'en-us':
          matchedLanguage = lingue.includes('inglese') ? 'inglese' : null;
          break;
            default:
          matchedLanguage = 'italiano';
        }
      }

      if (matchedLanguage) {
        set(this.applicationController, 'instr_lang', matchedLanguage);
        set(this, 'intLang', matchedLanguage);
      }
    }

    @computed('session.option_field.comprensori.[]')
    get compPrivate() {
      const comprensori = this.session.option_field?.comprensori;

      if (!Array.isArray(comprensori)) {
      return [];
      }

      return comprensori.filter(item => item.ecommerce_private === true);
    }

    @computed('session._orgSnapshot')
    get contactStateAltri() {
      return this.session.findContactState('114');
    }

    @computed('session.option_field.ecommerce_always_show_detail_filter', 'showPrivateFilter')
    get showPrivateFilterFinal() {
      const optionField = this.session.option_field;

      return this.showPrivateFilter || (optionField && optionField.ecommerce_always_show_detail_filter);
    }

    @computed('session.option_field.ecommerce_livello_minimo')
    get useLivelloMinimo() {
      return this.session.option_field?.ecommerce_livello_minimo;
    }

    @computed('session.option_field.ecommerce_collettive_single')
    get ecommerceCollettiveSingle() {
      return this.session.option_field?.ecommerce_collettive_single;
    }

    @computed('searchBy', 'privateData.fromTurno')
    get showCalendar() {
      return this.searchBy === 'date' && !this.privateData?.fromTurno;
    }

    @computed('searchBy', 'privateData.fromTurno')
    get showInstructor() {
      return this.searchBy === 'instructor' || this.privateData?.fromTurno;
    }

    @computed('item.set_event_color', 'useLivelloMinimo')
    get isLivelloMinimo() {
      const { useLivelloMinimo, item } = this;

      if (!useLivelloMinimo || !item) {
      return true;
      }

      return !item.set_event_color;
    }

    @computed('buyGroups.months.length', 'byGroupComp')
    get buyGroupsFiltered() {
      const { buyGroups, byGroupComp } = this;

      if (!Array.isArray(buyGroups?.months)) {
      return;
      }

      buyGroups.months.forEach(month => {
        month.events.forEach(event => {
          let hiddenCount = 0;

          set(event, 'showDay', null);

          event.dispos.forEach(dispo => {
            set(dispo, 'hide', false);

            if (byGroupComp && dispo.description !== byGroupComp) {
              set(dispo, 'hide', true);
              hiddenCount++;
            }
          });

          set(event, 'hide', false);

          if (hiddenCount === event.dispos.length) {
            set(event, 'hide', true);
          }
        });
      });

      return buyGroups.months;
    }

    @computed('item.id', 'this.session.openOrganization.id')
    get showSkeleton() {
      const { item, applicationController } = this;
      const { open_organization } = applicationController?.content || {};

      return !open_organization?.get('id') || !item?.id;
    }

    @computed(
      'applicationController.imgPrivate',
      'applicationController.imgCorsi',
      'item.{id,thumbnail_url,product_status_id}',
      'this.session.openOrganization.id'
    )
    get imgCopertina() {
      const { applicationController, item } = this;
      const { open_organization } = applicationController?.content || {};

      if (!open_organization?.get('id') || !item?.id) {
        return null;
      }

      if (item?.thumbnail_url) {
        return `${applicationController.urlData}/${item.thumbnail_url}-large.jpeg`;
      } else if (item?.product_status_id === 92) {
        return applicationController.imgPrivate;
      }

      return applicationController.imgCorsi;
    }

    @computed('session.option_field.hideAddToChart', 'item.product_status_id')
    get hideAddToChart() {
      const option_field = this.session.option_field;
      const { product_status_id } = this.item || {};

      if (option_field?.hideAddToChart && product_status_id) {
        return option_field.hideAddToChart[product_status_id];
      }

      return false;
    }

    @computed('session.option_field.ecommerce_private_min_max.min')
    get minPartecipanti() {
      const option_field = this.session.option_field;

      return option_field?.ecommerce_private_min_max?.min || 1;
    }

    @computed('session.option_field.ecommerce_private_min_max.max')
    get maxPartecipanti() {
      const option_field = this.session.option_field;

      return option_field?.ecommerce_private_min_max?.max || 10;
    }

    @computed('session.option_field.ecommerce_provate_ghost')
    get privateGhost() {
      const option_field = this.session.option_field;

      return option_field?.ecommerce_provate_ghost || false;
    }

    @computed('item.thumbnail_url')
    get thumbUrl() {
      const { item, applicationController } = this;
      const { thumbnail_url } = item || {};
      const { content } = applicationController || {};
      const { open_organization } = content || {};

      if (thumbnail_url) {
        return `${applicationController.urlData}/${thumbnail_url}-large.jpeg`;
      }

      const { thumbnail_url: orgThumbnailUrl } = open_organization || {};

      return `${applicationController.urlData}/${orgThumbnailUrl}-large.jpeg`;
    }

    @computed('item.first_dispo').readOnly()
    get firstDispo() {
      const { first_dispo } = this.item || {};

      return first_dispo ? moment(first_dispo).format('YYYY-MM-DD') : null;
    }

    @computed('id', 'applicationController.prodotti.products.length')
    get item() {
      const { id, applicationController } = this;
      const { prodotti } = applicationController || {};
      const { products } = prodotti || {};

      if (Array.isArray(products)) {
        return products.find(i => i.id === parseInt(id)) || null;
      }

      return null;
    }

    // Availability is validated as a side-effect-free task instead of inside the
    // `item` getter, so a transient empty products list can't bounce the user home.
    @(task(function* () {
      const { applicationController } = this;
      const loadDataTask = applicationController?.loadDataTask;

      if (loadDataTask?.isRunning) {
        yield loadDataTask.last;
      }

      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      const products = applicationController?.prodotti?.products;
      if (!Array.isArray(products)) {
        return;
      }

      if (this.item?.id) {
        this.loadPrivateTask.perform();
        return;
      }

      this.notify.warning(this.intl.t('Some products are no longer available'));
      this.router.transitionTo('index');
    }).restartable()) checkAvailabilityTask

    @(task(function* (lang = null) {

      try {
        yield timeout(250);

        if (this.item?.product_status_id !== 92) {
          return;
        }

        this.setDefLang();

        const first_dispo_description = this.item.first_dispo_description;
        if(this.compPrivate.length > 0 && this.compPrivate[0]?.name) {
          const defaultComp = first_dispo_description || this.compPrivate[0].name;
          const isPrivateComp = (name) => this.compPrivate.some(comp => comp.name === name);

          if (!this.applicationController.location || !isPrivateComp(this.applicationController.location)) {
            set(this, 'applicationController.location', defaultComp);
          }

          // `intComp` is seeded from the default ONLY while the customer has no
          // valid resort of their own. It used to be overwritten whenever
          // `applicationController.location` was empty — which is exactly the
          // ghost-mode case, where the resort control writes `intComp` and
          // nothing else. `loadPrivateTask` then ran on that very click and put
          // the product's default resort back, so the calendar reloaded hours
          // for a resort the customer had not asked for while their button
          // stopped looking selected.
          if (!isPrivateComp(this.intComp)) {
            set(this, 'intComp', this.applicationController.location);
          }
        }

        if (this.session.option_field?.ecommerce_provate_ghost) {
          set(this, 'reloadDataMaestro', !this.reloadDataMaestro);
          //return;
        }

        if(!this.intComp || this.intComp === this.applicationController.location) {
          set(this, 'intComp', this.applicationController.location);
        }

        // `inizio` is a plain calendar day everywhere else it is produced — the
        // fallbacks below, `buyGroupsTask`'s `from_date`, `loadDataTask`'s
        // `from_date` and <PrivateCalendar>'s own `_buildEvents`, which all
        // format to 'YYYY-MM-DD'. The shared `date` query param, however, is
        // written in TWO shapes: /list's calendar writes 'YYYY-MM-DD' while the
        // filter panel's <BbInputDate> writes a full ISO timestamp
        // ('2026-02-20T12:00:00+01:00'). Forwarding it raw meant the same picked
        // day reached this endpoint in a different format depending on which
        // control set it. Normalize here rather than in the shared date widget.
        let data = this.applicationController.date
          ? moment(this.applicationController.date).format('YYYY-MM-DD')
          : this.firstDispo || moment().format('YYYY-MM-DD');

        const product = yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/ecommerce_private_dashboard`,
          this.session.token,
          {
            product_id: this.item.product_id,
            inizio: data,
            lang: this.applicationController.instr_lang || null,
            gen: this.gender || null,
            comp: this.intComp || this.applicationController.location,
            q: this.privateQ || null,
            access_token: this.session.token,
          }
        );

        if (Array.isArray(product.maestri)) {
          product.maestri.forEach(maestro => {
            if (maestro.descrizioni) {
              try {
                maestro.descrizioniObj = JSON.parse(maestro.descrizioni);
              } catch (e) {
                this.log.error('detail: maestro descrizioni parse failed', e);
              }
            }

            // localita can be null/undefined for some instructors; toString also
            // ignores its argument (always joins with ','), so join(', ') is used.
            maestro.localitaStr = Array.isArray(maestro.localita)
              ? maestro.localita.join(', ')
              : (maestro.localita ?? '');
          });
        }

        /*if(!this.localita && Array.isArray(product.meta_localita) && product.meta_localita.length > 0) {
            set(this, 'localita', product.meta_localita[0].localita)
        }*/

        set(this, 'privateData', product);
      } catch (e) {
        // Left uncaught, a 4xx/network failure on the private dashboard load
        // surfaced as a Sentry unhandled rejection. Background loader: log only.
        this.log.warn('loadPrivateTask: /ecommerce_private_dashboard failed', e);
      }
    }).restartable()) loadPrivateTask

    @(task(function* (location = null) {
       yield timeout(50);

      set(this, 'compList', null);
      set(this, 'byGroupComp', location);

      const self = this;
      const url = `${getConfig(this).APP.urlApi}/ecommerce_dashboard`;

      // The API requires the token as a query parameter; the Authorization
      // header is sent as well for forward compatibility.
      const response = yield beebeeAjaxGet(
        url,
        self.session.token,
        {
          id: this.item.id,
          from_date: this.applicationController.date || moment().format('YYYY-MM-DD'),
          access_token: self.session.token
        }
      );

      const product = response?.data?.products?.[0];
      if (!product) {
        set(this, 'buyGroups', null);
        return;
      }
      const compList = [];

      for (const month of product.months || []) {
        const events = month.events || [];

        for (const event of events) {
          const dispos = event.dispos || [];
          const days = dispos.map((dispo) => {
            compList.push(dispo.description);
            return dispo.giorni;
          });

          set(event, 'listDay', [...new Set(days)]);
        }
      }

      const uniqueCompList = [...new Set(compList)];

      set(this, 'compList', uniqueCompList);
      set(this, 'buyGroups', product);

      if (this.item.product_status_id === 120) {
        const firstEvent = product?.months?.[0]?.events?.[0];
        const firstDispo = firstEvent?.dispos?.[0];

        if (firstDispo) {
          this.showBuyProductF(firstDispo);
          set(firstEvent, 'showDay', true);
        }
      }

    }).restartable()) buyGroupsTask

    @(task(function* (p) {
        try {
          yield timeout(500);

          var self = this;

          const prezzoAPI = yield beebeeAjaxPost(
              `${getConfig(this).APP.urlApi}/scuolasci_freeprice`,
              null,
              {
                  products: [{
                      data_inizio: moment(p.start_date).format('YYYY-MM-DDTHH:mmZ'),
                      giorni: p.giorni,
                      id: p.product_id,
                      product_status_id: p.product_status_id,
                      persone: p.partecipanti?.length || 0,
                      comprensorio: p.description,
                      thumbnail_url: p.thumbnail_url || '',
                      name: p.name || '',
                      assicurazione: false,
                      fisso: false,
                      prezzo_fisso: false,
                      partecipanti: [1],
                      modules: [1]
                  }],
                  access_token: self.session.token
              }
          )

          set(p, 'prezzoAPI', prezzoAPI.data)

          // Se il prodotto è già nel carrello, riassegna cart così il getter tracked totCart si ricalcola
          if (this.applicationController.cart?.groups?.includes(p)) {
            set(this.applicationController, 'cart', { ...this.applicationController.cart });
          }
        } catch (e) {
          // Left uncaught, a 4xx/network failure here surfaced as a Sentry
          // unhandled rejection. User-triggered (group price calc): notify.
          this.log.error('freeGroupPriceTask failed', e);
          const detail = e?.responseJSON?.errors?.[0]?.detail;
          this.notify.error(detail || this.intl.t('beebeeboard error'));
          return;
        }
    }).restartable()) freeGroupPriceTask

    @(task(function* () {
      try {
        yield timeout(50);
        const self = this;
        const events = [];

        yield timeout(500);

        if (this.oreSelected.length > 0) {
          for (const e of this.oreSelected) {
            if (!e.n_pax) {
              set(e, 'n_pax', 1);
            }

            const start_date = moment(e.start_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');
            const end_date = moment(e.end_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');

            events.push({
              partecipanti: '1',
              fisso: false,
              maestro_id: self.privateGhost ? '0' : self.buyMaestro.id,
              comprensorio: e.location,
              lingua: self.language,
              persone: e.n_pax,
              start_date,
              end_date
            });
          }
        }

        const data = {
          products: [{
            thumbnail_url: 't',
            partecipanti: '1',
            name: 'Individuale',
            persone: 0,
            product_status_id: this.item.product_status_id,
            id: this.item.product_id,
            assicurazione: false,
            events: events,
            modules: [1],
          }],
          access_token: self.session.token,
        };

        const prezzoAPI = yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/scuolasci_freeprice`,
          self.session.token,
          data
        );

        if (Array.isArray(prezzoAPI?.data?.products)) {
          const product = prezzoAPI.data.products[0];
          // products can be an empty array (backend returned no match): product is
          // then undefined and product.events threw
          // (Sentry EMBER-SCUOLE-ECOMMERCE-2XK). Guard both hops.
          (product?.events || []).forEach((event) => {
            if (self.oreSelected.length > 0) {
              self.oreSelected.forEach((e) => {
                if (
                  moment(e.start_date).tz('Europe/Rome', true).isSame(moment(event.start_date).tz('Europe/Rome'), 'minute')
                ) {
                  set(e, 'price', event.prezzo);
                }
              });
            }
          });
        }

        set(this, 'oreSelected.prezzoAPI', prezzoAPI?.data);

        if(prezzoAPI?.data?.products && prezzoAPI.data.products.some(product => product.product_status_id === 92 && product.maestro_diverso === true)) {
          this.notify.warning(this.intl.t('We cannot guarantee the same teacher'));
          set(this, 'oreSelected', []);
          set(this, 'buyMaestro', null);
          return false;
        }

        return true;
      } catch (e) {
        // Left uncaught, a 4xx/network failure here surfaced as a Sentry
        // unhandled rejection. User-triggered (private price calc): notify.
        this.log.error('freePrivatePriceTask failed', e);
        const detail = e?.responseJSON?.errors?.[0]?.detail;
        this.notify.error(detail || this.intl.t('beebeeboard error'));
        return false;
      }
    }).restartable()) freePrivatePriceTask

    @(task(function* () {
      yield timeout(50);
      const events = [];

      yield timeout(500);

      if (this.oreSelectedDate.length > 0) {
        for (const e of this.oreSelectedDate) {
          if (!e.n_pax) {
            set(e, 'n_pax', 1);
          }

          const start_date = moment(e.start_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');
          const end_date = moment(e.end_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');

          events.push({
            partecipanti: '1',
            fisso: false,
            //maestro_id: self.privateGhost ? '0' : self.buyMaestro.id,
            persone: e.n_pax,
            start_date,
            end_date,
          });
        }
      }

      const data = {
        product: {
          thumbnail_url: 't',
          partecipanti: '1',
          name: 'Individuale',
          persone: 0,
          product_status_id: this.item.product_status_id,
          id: this.item.product_id,
          comprensorio: this.intComp || this.applicationController.location,
          lingua: this.language,
          gender: this.gender,
          events: events
        }
      };

      if(!this.privateData) {
        set(this, 'privateData', {});
      }

      set(this, 'privateData.fromTurno', true);
      set(this, 'privateData.maestri', null);
      set(this, 'privateData.tot_maestri', {
        count: 0
      });

      try {
        const maestriAPI = yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/ecommerce_turno_maestro`,
          null,
          data
        );

        if (Array.isArray(maestriAPI.data?.maestri)) {
          maestriAPI.data.maestri.forEach(maestro => {
            if (maestro.descrizioni) {
              try {
                maestro.descrizioniObj = JSON.parse(maestro.descrizioni);
              } catch (e) {
                this.log.error('detail: maestro descrizioni parse failed', e);
              }
            }

            // Same guard as loadPrivateTask: null localita must not abort the
            // whole task (the catch below would report 'no instructor available').
            maestro.localitaStr = Array.isArray(maestro.localita)
              ? maestro.localita.join(', ')
              : (maestro.localita ?? '');
          });
        }

        set(this, 'privateData.maestri', maestriAPI.data?.maestri);
        set(this, 'privateData.tot_maestri', maestriAPI.data?.tot_maestri);
        this.scrollToAvailable();
      } catch(e) {
        this.notify.warning(this.intl.t('no instructor available'));
      }

    }).restartable()) maestroTurnoTask

    @(task(function* (dispo = null, individuali = false) {
      yield timeout(50);

      try {
        yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/ecommerce_check_product`,
          this.session.token,
          this.item.block_check_json
        );

        if (individuali) {
          this._addPrivateCart();
        } else {
          this.addGroupsCartTask.perform(dispo);
        }
      } catch (e) {
        this.log.error('detail: add cart failed', e);
      }
    }).restartable()) checkBlockTask;

    showBuyProductF(p) {
      const livelliFinal = [];
      let livelliTmp = [];

      if (Array.isArray(this.buyGroupsFiltered)) {
        this.buyGroupsFiltered.forEach((buyGroup) => {
          if (Array.isArray(buyGroup.events)) {
            buyGroup.events.forEach((event) => {
              if (!moment(event.start_date).isSame(moment(p.start_date), 'day') &&
                  event.giorni !== p.giorni) {
                set(event, 'showDay', false);
              }

              if (Array.isArray(event.dispos)) {
                event.dispos.forEach((dispo) => {
                  set(dispo, 'showBuy', false);
                });
              }
            });
          }
        });
      }

      if (Array.isArray(p.livelli)) {
        p.livelliObj = p.livelli;
      } else if (typeof p.livelli === 'string' || p.livelli instanceof String) {
        try {
            p.livelliObj = JSON.parse(p.livelli);
          } catch (e) {
            p.livelliObj = null;
          }
      } else {
        p.livelliObj = null;
      }

      if (Array.isArray(p.livelliObj)) {
        livelliTmp = p.livelliObj
          .filter((l) => l.players_number > 0)
          .map(item => item.livello_name);
      } else {
        livelliTmp = this.session.option_field?.livelli_sci || [];

        if (this.item?.is_snow) {
          livelliTmp = livelliTmp.filter(item => item.isSnowboard === true);
        } else if (this.item.is_fondo) {
          livelliTmp = livelliTmp.filter(item => item.isFondo === true);
        }

        if (this.item?.is_sci || (!this.item?.is_fondo && !this.item?.is_snow)) {
          livelliTmp = livelliTmp.filter((l) => !l.hide_ecommerce && !l.hideSci);
        }

        livelliTmp = livelliTmp
          .filter((l) => !l.hide_ecommerce)
          .map(item => item.colorName);
      }

      livelliTmp.forEach((livelloTmp) => {
        const livello = (this.session.option_field?.livelli_sci || []).find(i => i.colorName === livelloTmp);
        livelliFinal.push(livello);
      });

      set(p, 'livelliArr', livelliFinal);

      let livelliDesc = 'description';
      if (this.item.is_snow) {
        livelliDesc += '_snow';
      } else if (this.item.is_fondo) {
        livelliDesc += '_fondo';
      }

      if (this.applicationController.lang === 'en-US' || this.applicationController.lang === 'en-GB') {
        livelliDesc += '_en';
      } else if (this.applicationController.lang === 'fr-FR') {
        livelliDesc += '_fr';
      }  else if (this.applicationController.lang === 'de-DE') {
        livelliDesc += '_de';
      }

      set(p, 'livelliDesc', livelliDesc);
      set(p, 'partecipanti', [{
        livello: null
      }]);

      if (!p.min_players_number) {
        p.min_players_number = 1;
      }

      p.partecipanti = Array.from({ length: p.min_players_number }, () => ({ livello: null }));

      const select = p.showBuy;

      if (Array.isArray(this.buyGroups?.events)) {
        set(this, 'totLevel', []);

        this.buyGroups.events.forEach((event) => {
          event.dispos.forEach((dispo) => {
            set(dispo, 'showBuy', false);
          });
        });
      }

      set(p, 'showBuy', !select);

      this.freeGroupPriceTask.perform(p);
    }

    @(task(function* (searchBy) {
        set(this, 'searchBy', searchBy);
        if(this.privateData) {
          set(this, 'privateData.fromTurno', false);
        }

        yield timeout(250);

        if(searchBy === 'instructor') {
          this.loadPrivateTask.perform();
        }

        let targetEl = null;

        if(searchBy === 'instructor') {
          targetEl = document.getElementById('instAvalableDiv');
        } else {
          targetEl = document.getElementById('calendarDateDiv');
        }

        if (targetEl) {
          const rect = targetEl.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          window.scrollTo({ top, behavior: 'smooth' });
        }
    }).restartable()) searchByTask;

    @action
    setComp(comp) {
      set(this, 'applicationController.location', comp);
      set(this, 'intComp', comp);

      this.loadPrivateTask.perform();
      set(this, 'reloadDataMaestro', !this.reloadDataMaestro);
    }

    @action
    setLang(lang) {
      set(this, 'applicationController.instr_lang', lang);
      set(this, 'intLang', lang);

      this.loadPrivateTask.perform();
      set(this, 'reloadDataMaestro', !this.reloadDataMaestro);

    }

    @action
    showBuyProduct(p) {
        this.showBuyProductF(p, event);
    }

    @action
    addParPrivate() {
        for(let i = 0; i < this.oreSelected.length; i++) {
            if(this.oreSelected[i].n_pax) {
                set(this.oreSelected[i], 'n_pax', this.oreSelected[i].n_pax + 1);
            }

            if(!this.oreSelected[i].n_pax || this.oreSelected[i].n_pax > this.maxPartecipanti) {
                set(this.oreSelected[i], 'n_pax', this.maxPartecipanti);
            }
        }

        this.freePrivatePriceTask.perform();
    }

    @action
    delParPrivata() {

        for(let i = 0; i < this.oreSelected.length; i++) {
            if(this.oreSelected[i].n_pax) {
                set(this.oreSelected[i], 'n_pax', this.oreSelected[i].n_pax - 1);
            }

            if(!this.oreSelected[i].n_pax || this.oreSelected[i].n_pax < this.minPartecipanti) {
                set(this.oreSelected[i], 'n_pax', this.minPartecipanti);
            }
        }

        this.freePrivatePriceTask.perform();
    }

    @action
    addPartecipante(d) {
        if(!d.partecipanti) {
            set(d, 'partecipanti', [])
        }

        if(d.partecipanti.length >= d.max_players_number) {
            this.notify.warning(this.intl.t('max pax reached'));
            return;
        }

        set(d, 'partecipanti', [...d.partecipanti, { livello: null }]);

        this.freeGroupPriceTask.perform(d);
    }

    @action
    delPartecipante(d) {
        if(!d.partecipanti || d.partecipanti.length <= d.min_players_number) {
            this.notify.warning(this.intl.t('max pax reached'));
            set(d, 'partecipanti', []);

            if(!d.min_players_number) {
                d.min_players_number = 1;
            }

            const partecipanti = [];
            for(let i = 0; i < d.min_players_number; i++) {
                partecipanti.push({
                    livello: null
                });
            }
            set(d, 'partecipanti', partecipanti);
        } else {
            set(d, 'partecipanti', d.partecipanti.slice(0, -1));
        }

        this.freeGroupPriceTask.perform(d);
    }

    @action
    delLevel(p) {
      const type = this.item.is_snow ? 'snow_color' : this.item.is_fondo ? 'fondo_color' : 'level';

      if (this.totLevel && this.totLevel[p[type].colorName]) {
        this.totLevel[p[type].colorName]--;
      }

      set(p, type, null);
    }

    @action
    setLevel(contact, dispo, level) {
      let parentLevel = null;

      if (level) {
        parentLevel = dispo.livelliArr.find(l =>
          l.sotto_livelli && l.sotto_livelli.some(sub => sub.colorName === level.colorName)
        );
      }

      if (!level) {
        if (this.item.is_snow) {
          set(contact, 'snow_color', null);
        } else if (this.item.is_fondo) {
          set(contact, 'fondo_color', null);
        } else {
          set(contact, 'level', null);
        }
        return;
      }

      if (!this.totLevel) {
        this.totLevel = [];
      }

      const effectiveLevel = parentLevel || level;

      if (!this.totLevel[effectiveLevel.colorName]) {
        this.totLevel[effectiveLevel.colorName] = 0;
      }

      this.totLevel[effectiveLevel.colorName]++;

      let available = true;
      const type = this.item.is_snow ? 'snow_color' : this.item.is_fondo ? 'fondo_color' : 'level';

      if (Array.isArray(dispo[type])) {
        const lf = dispo[type].filter(item => item.livello_name === effectiveLevel.colorName);
        const maxP = lf?.[0]?.players_number || 0;

        if (this.totLevel[effectiveLevel.colorName] > maxP) {
          available = false;
        }
      }

      if (!available) {
        this.notify.warning(this.intl.t('level not available'));
        return;
      }

      set(contact, type, level);
    }

    _addPrivateCart() {
        const bloccoOre = {
            maestro: this.buyMaestro,
            item: this.item,
            comprensorio: this.intComp || this.applicationController.location,
            language: this.intLang,
            gender: this.gender,
            prezzoAPI: this.oreSelected.prezzoAPI,
            ore: []
        };

        for(let i = 0; i < this.oreSelected.length; i++) {
            bloccoOre.ore.push(this.oreSelected[i]);
        }

        this.applicationController.addCart('private', bloccoOre);
        set(this, 'buyMaestro', null);
    }

    @action
    addPrivateCart() {
        this._addPrivateCart();
    }

    @(task(function* (p = null) {
      let pCompleti = true;

      if (this.applicationController.ecommerceLoginBefore) {
        if (!this.session.me?.get('id')) return;
        const c = yield this.store.findRecord('contact', this.session.me?.get('id'), { reload: true });
        const related_contacts = yield c.get('related_contacts');
        const firstContactAltri = related_contacts.filter(item => item.get('contact_state.id') === this.contactStateAltri?.id);

        if (firstContactAltri[0]) {
          p.partecipanti = [{
            level: firstContactAltri[0].color
          }];
        }
      }

      const hideLevel = this.session.option_field?.ecommerce_hide_level;
      const isRaidaComeMangi = this.session.option_field?.isRaidaComeMangi;

      if (!hideLevel && !isRaidaComeMangi) {
        for (let i = 0; i < p.partecipanti.length; i++) {
          set(p.partecipanti[i], 'error', false);

          if (this.item.is_snow) {
            if (!p.partecipanti[i].snow_color) {
              set(p.partecipanti[i], 'error', true);
              pCompleti = false;
            }
          } else if (this.item.is_fondo) {
            if (!p.partecipanti[i].fondo_color) {
              set(p.partecipanti[i], 'error', true);
              pCompleti = false;
            }
          } else {
            if (!p.partecipanti[i].level && p.partecipanti[i].level !== 0) {
              set(p.partecipanti[i], 'error', true);
              pCompleti = false;
            }
          }
        }

        if (!pCompleti || !p.partecipanti || p.partecipanti.length === 0) {
          this.notify.warning(this.intl.t('select your level'));
          return;
        }
      }

      p.is_pacchetto = this.item.product_status_id === 120;
      p.item = this.item;
      p.comprensorio = p.comprensorio;

      if (isRaidaComeMangi) {
        const product = yield this.store.findRecord('product', p.item.id, { reload: true });
        const only_once = cfFilterId(product.custom_fields, '491');
        p.only_once = only_once.boolean_value;

        if (only_once.boolean_value && Array.isArray(this.applicationController.cart?.groups)) {
          for (let i = 0; i < this.applicationController.cart.groups.length; i++) {
            if (this.applicationController.cart.groups[i].only_once &&
              this.applicationController.cart.groups[i].item.id === p.item.id) {
              this.notify.warning(this.intl.t('for this category you can only buy one product at a time'));
              return;
            }
          }
        }
      }

      if (this.ecommerceCollettiveSingle) {
        if (Array.isArray(this.applicationController.cart?.groups)) {
          for (let i = 0; i < this.applicationController.cart.groups.length; i++) {
            if (this.applicationController.cart.groups[i].item.product_status_id === p.item.product_status_id) {
              this.notify.warning(this.intl.t('for this category you can only buy one product at a time'));
              return;
            }
          }
        }
      }

      const singleLevelDispo = Array.isArray(p.livelli);
      const inCart = {};
      const groups = this.applicationController.cart?.groups;
      if (Array.isArray(groups)) {
        groups.forEach((item) => {
          if (item.id === p.id && item.start_date === p.start_date && item.description === p.description && Array.isArray(item?.partecipanti)) {
            item.partecipanti.forEach((pax) => {
              if(singleLevelDispo && pax.level) {
                const colorName = pax.level.colorName;
                inCart[colorName] = (inCart[colorName] || 0) + 1;
              } else {
                inCart['tot'] = (inCart['tot'] || 0) + 1;
              }
            });
          }
        });
      }

      const toAddCart = {};
      if (Array.isArray(p?.partecipanti)) {
        p.partecipanti.forEach((pax) => {
          if(singleLevelDispo && pax.level) {
            const colorName = pax.level.colorName;
            toAddCart[colorName] = (toAddCart[colorName] || 0) + 1;
          } else {
            toAddCart['tot'] = (toAddCart['tot'] || 0) + 1;
          }
        });
      }

      const totCart = this.sommaLivelliComuni(inCart, toAddCart);
      let canAdd = false;

      if(singleLevelDispo) {
        canAdd = this.confrontaLivelli(totCart, p.livelli);
      } else {
        canAdd = totCart['tot'] <= (p.tot_players_number || 0);
      }

      if(canAdd) {
        this.applicationController.addCart('groups', p);

        set(p, 'showBuy', null);
        set(this, 'buyGroups', null);
        set(this, 'applicationController.showCart', true);
      } else {
        this.notify.warning(this.intl.t('not enough availability'));
        return false;
      }
    }).restartable())
    addGroupsCartTask

    confrontaLivelli(risultatoSomma, livelli) {
      let canAdd = true;

      for (let i = 0; i < livelli.length; i++) {
        const livello = livelli[i];
        const risultatoValue = risultatoSomma[livello.livello_name];

        if (risultatoValue !== undefined && risultatoValue > livello.players_number) {
          canAdd = false
        }
      }

      return canAdd;
    }

    sommaLivelliComuni(obj1, obj2) {
      const risultato = { ...obj1 };

      for (const chiave in obj2) {
        if (risultato.hasOwnProperty(chiave)) {
          risultato[chiave] += obj2[chiave];
        } else {
          risultato[chiave] = obj2[chiave];
        }
      }

      return risultato;
    }

    @action
    addGroupsCart(p) {
        this.addGroupsCartTask.perform(p);
    }

    @action
    showBuyEvent(event) {
        if(Array.isArray(this.buyGroupsFiltered)) {
            for(let i = 0; i < this.buyGroupsFiltered.length; i++) {
                if(Array.isArray(this.buyGroupsFiltered[i].events)) {
                    for(let j = 0; j < this.buyGroupsFiltered[i].events.length; j++) {
                        if(!moment(this.buyGroupsFiltered[i].events[j].start_date).isSame(moment(event.start_date), 'day')) {
                            set(this.buyGroupsFiltered[i].events[j], 'showDay', false);
                        }

                        if(Array.isArray(this.buyGroupsFiltered[i].events[j].dispos)) {
                            for(let k = 0; k < this.buyGroupsFiltered[i].events[j].dispos.length; k++) {
                                set(this.buyGroupsFiltered[i].events[j].dispos[k], 'showBuy', false);
                            }
                        }
                    }
                }
            }
        }

        set(event, 'showDay', !event.showDay);

        if(this.item.product_status_id === 108) {
            this.showBuyProductF(event.dispos[0], event);
        }
    }

    @action
    setReload(variable, value, variable1 = null, value1 = null, type = null) {
        set(this, `applicationController.${variable}`, value);

        if(variable1) {
            set(this, `applicationController.${variable1}`, value1);
        }

        if(type) {
            set(this, 'type', type);
        }

        this.applicationController.loadDataTask.perform();
    }

    @action
    setFilter(variable, value) {
        set(this, variable, value);
        this.loadPrivateTask.perform();

        set(this, 'reloadDataMaestro', !this.reloadDataMaestro);

    }

    @action
    setDate(date) {
        set(this, 'applicationController.date', date);
        this.loadPrivateTask.perform();
        set(this, 'reloadDataMaestro', !this.reloadDataMaestro);
    }

    @action
    requestMaeMail(side = 'right') {
        if(this.applicationController.showRichiediInfo) {
          let msg = '';
          // Dashboard product objects expose product_status_id (number), not
          // a product_state relationship — see checkAvailabilityTask above.
          if(this.item?.product_status_id === 92) {
            msg = `Buongiorno, vorrei chiedere se è disponibile un maestro ${this.applicationController.location ? `a ${this.applicationController.location}` : 'in qualunque località'} ${this.intLang ? `che parli ${this.intLang}` : ''} ${this.gender ? `possibilimente di sesso ${this.gender}` : ''}
Vi ringrazio`;
          } else {
            msg = `Buongiorno, vorrei chiedere se è disponibile il corso ${this.item.name}
Vi ringrazio`;
          }
          set(this, 'applicationController.contact_message', msg);
          set(this, 'applicationController.noDispoOnline', true);
        }
    }

    @action
    sideScroll(side = 'right') {
        const delta = side === 'right' ? 500 : -500;
        const el = document.getElementById('slide-maestri');

        if (el) {
          el.scrollBy({ left: delta, behavior: 'smooth' });
        }
    }

    @action
    scrollToAvailable() {
        const targetEl = document.getElementById('instAvalableDiv');

        if (targetEl) {
          const rect = targetEl.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          window.scrollTo({ top, behavior: 'smooth' });
        }
    }

}
